import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import PaywallModal from '../components/PaywallModal';
import UpgradeModal from '../components/UpgradeModal';
import OpportunityLinkerModal from '../components/OpportunityLinkerModal';
import OpportunityCard from '../components/opportunities/OpportunityCard';
import UpgradeBanner from '../components/UpgradeBanner';
import Tooltip from '../components/Tooltip';
import ReportBuilderModal from '../components/modals/ReportBuilderModal';
import AssessmentSuccessBanner from '../components/AssessmentSuccessBanner';
import RoadmapVisual from '../components/RoadmapVisual';
import { usePlan } from '../context/PlanContext';
import { useOrganization, clearCompletionInProgress } from '../context/OrganizationContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowRight,
  CircleNotch,
  CurrencyDollar,
  FileText,
  GearSix,
  Lightning,
  Lock,
  Rocket,
  Stack,
  Target,
  Trophy
} from '@phosphor-icons/react';
// ═══════════════════════════════════════════════════════════════════════════════
// COMPLETION LOCK HELPERS
// Hard lock that prevents any redirect back to wizard after completion
// Only cleared on explicit user action (button click)
// ═══════════════════════════════════════════════════════════════════════════════

const getCompletionLock = (orgId) => {
  try {
    const key = `assessment_completion_lock_${orgId || 'default'}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    // Check if expired
    if (new Date(data.expiresAt) < new Date()) {
      sessionStorage.removeItem(key);
      console.log('[COMPLETION_LOCK_EXPIRED]', { key });
      return null;
    }
    return data;
  } catch (e) {
    console.error('[COMPLETION_LOCK_ERROR] Failed to read lock:', e);
    return null;
  }
};

const clearCompletionLock = (orgId, reason) => {
  const key = `assessment_completion_lock_${orgId || 'default'}`;
  sessionStorage.removeItem(key);
  console.log('[COMPLETION_LOCK_CLEAR]', { reason, key });
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPLETION SNAPSHOT HELPERS
// Used as fallback when backend fetch is slow or circuit breaker reduced output
// 
// TWO-STATE MODEL:
// - scanStatus: 'success' = assessment completed (PRIMARY indicator)
// - buildableStatus: 'success' | 'timeout' | 'error' | 'pending'
//   - timeout/error does NOT invalidate assessment
//   - only affects messaging
// ═══════════════════════════════════════════════════════════════════════════════

const getCompletionSnapshot = (orgId, assessmentId) => {
  try {
    const key = `assessment_completion_${orgId || 'default'}_${assessmentId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    // Snapshot is valid for 30 minutes (use createdAt or timestamp for backwards compat)
    const timestamp = data.createdAt || data.timestamp;
    const isRecent = Date.now() - timestamp < 30 * 60 * 1000;
    
    if (isRecent && data.assessmentId === assessmentId) {
      console.log('[AssessmentResults] Found completion snapshot:', data);
      return data;
    }
    return null;
  } catch (e) {
    console.error('[AssessmentResults] Error loading completion snapshot:', e);
    return null;
  }
};

const clearCompletionSnapshot = (orgId, assessmentId) => {
  const key = `assessment_completion_${orgId || 'default'}_${assessmentId}`;
  sessionStorage.removeItem(key);
  console.log('[AssessmentResults] Cleared completion snapshot:', key);
};

/**
 * Get the appropriate modal message based on two-state model
 * 
 * @param {number} totalOpportunities - Total opportunities identified
 * @param {string} buildableStatus - 'success' | 'timeout' | 'error' | 'pending'
 * @param {number|null} buildableCount - Number of buildable opportunities (if known)
 * @returns {object} { title, subtitle, showBuildableWarning }
 * 
 * IMPORTANT: Never show "No Opportunities Identified" if totalOpportunities > 0
 * Timeout should show "processing timed out, showing saved results", NOT "none found"
 */
const getModalMessage = (totalOpportunities, buildableStatus, buildableCount) => {
  console.log('[MODAL_STATE] totalOpps=' + totalOpportunities + ' buildableStatus=' + buildableStatus + ' buildableCount=' + buildableCount);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CASE 1: Zero opportunities - ONLY show "No Opportunities" when total is truly 0
  // This MUST be checked with strict equality and NOT triggered by timeout
  // ═══════════════════════════════════════════════════════════════════════════════
  if (totalOpportunities === 0 && buildableStatus !== 'timeout' && buildableStatus !== 'pending') {
    return {
      title: 'No Opportunities Identified',
      subtitle: 'Try adding more detail about your processes, pain points, and tools to get better results.',
      showBuildableWarning: false,
      isPartialSuccess: false
    };
  }
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // All cases below: totalOpportunities > 0 (or timeout while still loading)
  // Title always shows the count - NEVER say "No Opportunities" here
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Case 2: Buildable validation timed out (PARTIAL SUCCESS - still show count!)
  if (buildableStatus === 'timeout') {
    return {
      title: totalOpportunities > 0 
        ? `${totalOpportunities} Opportunities Identified`
        : 'Assessment Complete',
      subtitle: 'Buildable mapping timed out. Your results are saved — you can still review opportunities.',
      showBuildableWarning: true,
      isPartialSuccess: true
    };
  }
  
  // Case 3: Buildable validation errored (PARTIAL SUCCESS - still show count!)
  if (buildableStatus === 'error') {
    return {
      title: totalOpportunities > 0 
        ? `${totalOpportunities} Opportunities Identified`
        : 'Assessment Complete',
      subtitle: 'Buildable mapping unavailable. Your results are saved — you can still review opportunities.',
      showBuildableWarning: true,
      isPartialSuccess: true
    };
  }
  
  // Case 4: Buildable validation still pending (show count if available)
  if (buildableStatus === 'pending') {
    return {
      title: totalOpportunities > 0 
        ? `${totalOpportunities} Opportunities Identified`
        : 'Assessment Complete',
      subtitle: totalOpportunities > 0 
        ? 'Calculating buildable opportunities...'
        : 'Loading your results...',
      showBuildableWarning: totalOpportunities > 0,
      isPartialSuccess: totalOpportunities > 0
    };
  }
  
  // Case 5: Buildable validation completed successfully with zero buildable
  if (buildableStatus === 'success' && buildableCount === 0) {
    return {
      title: `${totalOpportunities} Opportunities Identified`,
      subtitle: 'No buildable opportunities on your current plan. Upgrade to access more.',
      showBuildableWarning: false,
      isPartialSuccess: false
    };
  }
  
  // Case 6: Full success with buildable opportunities
  return {
    title: `${totalOpportunities} Opportunities Identified`,
    subtitle: buildableCount !== null 
      ? `${buildableCount} buildable on your plan`
      : 'Review your AI automation opportunities',
    showBuildableWarning: false,
    isPartialSuccess: false
  };
};

const AssessmentResults = () => {
  const { scanId, assessmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const actualId = assessmentId || scanId; // Support both old and new route params
  const navigate = useNavigate();
  const { userPlan, planLimits, isFreePlan, tier, isProOrHigher, isBuilderOrHigher } = usePlan();
  const { activeOrganization: orgFromContext } = useOrganization();
  // DISABLED: useOrgSwitchRedirect causes redirects - we handle org context directly
  // const { isReady, activeOrganization, orgLoading } = useOrgSwitchRedirect();
  const activeOrganization = orgFromContext;
  const isReady = !!orgFromContext;
  const orgLoading = false;
  
  // Check if user is on Basic tier (for CTA gating)
  // Builder and above have full access to opportunities and insights
  const isBasicTier = isFreePlan();
  
  const [loading, setLoading] = useState(true);
  const [scanData, setScanData] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedCertainty, setSelectedCertainty] = useState('all'); // V2.2: Integration certainty filter
  const [showPaywall, setShowPaywall] = useState(false);
  const [totalOpportunityCount, setTotalOpportunityCount] = useState(0);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [planLimitsFromAPI, setPlanLimitsFromAPI] = useState(null);
  const [showLinkerModal, setShowLinkerModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isStillGenerating, setIsStillGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [generationCount, setGenerationCount] = useState(0);  // Current count during generation
  const generationPollRef = useRef(null);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // STICKY COUNTS: Once set, these NEVER get overwritten by timeouts/errors
  // This prevents the bug where buildable timeout resets total to 0
  // ═══════════════════════════════════════════════════════════════════════════════
  const stickyTotalRef = useRef(null);
  const stickyComingSoonRef = useRef(null);
  const stickyProcessRef = useRef(null);
  const stickyRoleRef = useRef(null);
  
  // Set sticky counts only once - they can never be reduced
  const setStickyCount = (ref, value) => {
    if (value !== null && value !== undefined && value > 0) {
      if (ref.current === null || value > ref.current) {
        console.log('[STICKY_COUNT] Setting sticky count:', { old: ref.current, new: value });
        ref.current = value;
      }
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // TERMINAL BUILDABLE STATUS: Status can only move forward, never backward
  // Prevents success -> timeout overwrites that cause wrong modal messaging
  // 
  // ALLOWED: idle/loading -> success, idle/loading -> timeout, idle/loading -> error
  // NOT ALLOWED: success -> timeout, success -> error, timeout -> success
  // ═══════════════════════════════════════════════════════════════════════════════
  const terminalBuildableStatusRef = useRef(null);
  
  const setTerminalBuildableStatus = (newStatus) => {
    const current = terminalBuildableStatusRef.current;
    
    // First status - always allow
    if (current === null || current === 'pending' || current === 'loading') {
      terminalBuildableStatusRef.current = newStatus;
      console.log('[BUILDABLE_STATUS] Set to:', newStatus);
      return newStatus;
    }
    
    // If current is 'success', don't allow downgrade to timeout/error
    if (current === 'success' && (newStatus === 'timeout' || newStatus === 'error')) {
      console.log('[BUILDABLE_STATUS_IGNORED] attempted=' + newStatus + ' current=' + current);
      return current; // Return current, don't update
    }
    
    // If current is 'timeout' or 'error', only allow upgrade to 'success' via explicit user action
    if ((current === 'timeout' || current === 'error') && newStatus === 'success') {
      // This should only happen if user re-runs validation
      terminalBuildableStatusRef.current = newStatus;
      console.log('[BUILDABLE_STATUS] Upgraded from', current, 'to:', newStatus);
      return newStatus;
    }
    
    // Otherwise keep current
    console.log('[BUILDABLE_STATUS] Keeping current:', current, '(attempted:', newStatus + ')');
    return current;
  };
  
  const getTerminalBuildableStatus = () => terminalBuildableStatusRef.current || 'pending';
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // REDIRECT LOCK: If success=1 or processing=1, NO redirect to wizard is allowed
  // This is the "killer safeguard" - only explicit user action can navigate away
  // ═══════════════════════════════════════════════════════════════════════════════
  const hasSuccessParam = searchParams.get('success') === '1';
  const hasProcessingParam = searchParams.get('processing') === '1';
  const redirectLocked = useRef(hasSuccessParam || hasProcessingParam);
  
  // Update redirect lock when params change
  useEffect(() => {
    if (hasSuccessParam || hasProcessingParam) {
      redirectLocked.current = true;
      console.log('[RESULTS_REDIRECT_LOCK] Redirect lock ENABLED - success=1 or processing=1 present');
    }
  }, [hasSuccessParam, hasProcessingParam]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // OPTION 2 UX: Processing state for immediate navigation
  // When processing=1, show skeleton cards and poll for updates
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (hasProcessingParam) {
      console.log('[OPTION2_UX] Processing param detected, enabling generation polling');
      setIsStillGenerating(true);
      setGenerationProgress('Analyzing your business data...');
    }
  }, [hasProcessingParam]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SUCCESS BANNER STATE - Simple, persistent banner at top of results
  // No modal, no timing issues, no HMR conflicts
  // ═══════════════════════════════════════════════════════════════════════════════
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  
  // Check if banner was dismissed for this assessment
  const wasBannerDismissed = useCallback(() => {
    try {
      const key = `assessment_banner_dismissed_${actualId}`;
      return sessionStorage.getItem(key) === 'true';
    } catch (e) {
      return false;
    }
  }, [actualId]);
  
  // Show banner when success=1 is in URL and banner wasn't dismissed
  useEffect(() => {
    const successParam = searchParams.get('success');
    if (successParam === '1' && !wasBannerDismissed()) {
      setShowSuccessBanner(true);
      console.log('[SUCCESS_BANNER] Showing banner for assessment:', actualId);
    }
  }, [searchParams, actualId, wasBannerDismissed]);
  
  // Handle banner dismiss
  const handleDismissBanner = () => {
    console.log('[SUCCESS_BANNER] User dismissed banner');
    setShowSuccessBanner(false);
    
    // Mark as dismissed in sessionStorage
    try {
      sessionStorage.setItem(`assessment_banner_dismissed_${actualId}`, 'true');
    } catch (e) {
      // Ignore sessionStorage errors
    }
    
    // Clear completion locks
    const orgId = activeOrganization?.organization_id || orgFromContext?.organization_id;
    window.__MM_COMPLETION_IN_PROGRESS = false;
    clearCompletionInProgress();
    clearCompletionLock(orgId, 'banner_dismissed');
    clearCompletionSnapshot(orgId, actualId);
    
    // Remove success=1 from URL
    navigate(`/assessments/results/${actualId}`, { replace: true });
  };
  
  // DEBUG: Log mount
  useEffect(() => {
    console.log('[RESULTS_MOUNT]', {
      id: actualId,
      success: searchParams.get('success'),
      path: window.location.pathname,
      search: window.location.search,
      timestamp: new Date().toISOString()
    });
  }, [actualId, searchParams]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // FETCH SCAN DATA - Define BEFORE the useEffect that uses it
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const fetchScanData = useCallback(async () => {
    console.log('[RESULTS_FETCH] Starting fetch for:', actualId);
    try {
      setLoading(true);
      setFetchError(null);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessments/${actualId}`,
        {},
        activeOrganization?.organization_id
      );

      console.log('[RESULTS_FETCH]', { status: response.ok ? 'ok' : 'error', code: response.status });

      if (!response.ok) {
        // ═══════════════════════════════════════════════════════════════════════════════
        // NEVER REDIRECT FROM RESULTS PAGE - Show error state instead
        // ═══════════════════════════════════════════════════════════════════════════════
        
        if (redirectLocked.current) {
          console.log('[RESULTS_REDIRECT_BLOCKED] reason=redirect_locked code=' + response.status);
        }
        
        // Check for completion snapshot as fallback
        const orgId = activeOrganization?.organization_id || orgFromContext?.organization_id;
        const snapshot = getCompletionSnapshot(orgId, actualId);
        
        if (snapshot) {
          console.log('[RESULTS_FETCH] Using completion snapshot as fallback:', snapshot);
          setScanData({
            id: actualId,
            company_name: 'Your Assessment',
            status: snapshot.status,
            total_opportunities: snapshot.opportunityCountTotal
          });
          setTotalOpportunityCount(snapshot.opportunityCountTotal || 0);
          setLoading(false);
          return;
        }
        
        // No snapshot - show error state but DO NOT REDIRECT
        console.log('[RESULTS_FETCH] No snapshot, showing error state (NOT redirecting)');
        setFetchError({
          code: response.status,
          message: response.status === 404 
            ? 'Assessment not found. It may still be processing.'
            : 'Failed to load results. Please try again.'
        });
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('[RESULTS_FETCH] Success - loaded', data.opportunities?.length || 0, 'opportunities');
      setScanData(data.scan);
      setOpportunities(data.opportunities || []);
      setTotalOpportunityCount(data.total_opportunity_count || data.opportunities?.length || 0);
      setPlanLimitsFromAPI(data.plan_limits || null);
      setFetchError(null);
    } catch (error) {
      console.error('[RESULTS_FETCH] Exception:', error);
      // Show error state but DO NOT REDIRECT
      setFetchError({
        code: 500,
        message: 'Failed to load results. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [actualId, activeOrganization?.organization_id, orgFromContext?.organization_id]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // GENERATION STATUS POLLING - Check if assessment is still generating
  // If user arrives before generation completes, poll and auto-refresh
  // ═══════════════════════════════════════════════════════════════════════════════
  
  const checkGenerationStatus = useCallback(async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/assessment/generation-status/${actualId}`,
        { method: 'GET' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const statusData = await response.json();
        console.log('[GENERATION_STATUS] Check:', statusData);
        
        // Update generation count
        const oppCount = statusData.opportunitiesCount || statusData.opportunities_count || 0;
        setGenerationCount(oppCount);
        
        if (statusData.status === 'processing' || statusData.status === 'pending' || statusData.status === 'PROCESSING') {
          // Still generating - update progress and continue polling
          setIsStillGenerating(true);
          
          // Build progress message with count
          let progressMsg = statusData.currentStep || statusData.progress_message || 'Generating opportunities...';
          if (oppCount > 0) {
            progressMsg = `Generated ${oppCount} of 5 opportunities. ${statusData.currentStep || 'Processing...'}`;
          }
          setGenerationProgress(progressMsg);
          return false; // Not complete
        } else if (statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'COMPLETED' || statusData.status === 'FAILED') {
          // Generation complete - refresh the page data
          setIsStillGenerating(false);
          setGenerationProgress(null);
          console.log('[GENERATION_STATUS] Complete, refreshing data...');
          
          // Update URL to replace processing=1 with success=1
          if (hasProcessingParam) {
            navigate(`/assessments/results/${actualId}?success=1`, { replace: true });
          }
          
          await fetchScanData();
          return true; // Complete
        }
      }
    } catch (error) {
      console.error('[GENERATION_STATUS] Error checking status:', error);
    }
    return false;
  }, [actualId, activeOrganization?.organization_id, fetchScanData, hasProcessingParam, navigate]);
  
  // Start polling if we detect the assessment is still generating OR processing=1 param
  useEffect(() => {
    // Check if assessment is still generating
    const shouldPollFromData = scanData && (
      scanData.status === 'generating' || 
      scanData.status === 'PROCESSING' ||
      scanData.generation_status === 'processing' ||
      scanData.generation_status === 'pending'
    );
    
    // Also poll if processing=1 param is present (Option 2 UX)
    const shouldPoll = shouldPollFromData || hasProcessingParam || isStillGenerating;
    
    if (shouldPoll && !generationPollRef.current) {
      console.log('[GENERATION_STATUS] Starting poll for assessment status...');
      setIsStillGenerating(true);
      
      // Start polling every 3 seconds for faster UX
      generationPollRef.current = setInterval(async () => {
        const complete = await checkGenerationStatus();
        if (complete && generationPollRef.current) {
          clearInterval(generationPollRef.current);
          generationPollRef.current = null;
        }
      }, 3000);
    }
    
    // Cleanup
    return () => {
      if (generationPollRef.current) {
        clearInterval(generationPollRef.current);
        generationPollRef.current = null;
      }
    };
  }, [scanData, hasProcessingParam, isStillGenerating, checkGenerationStatus]);
  
  // Trigger fetch on mount - MUST be after fetchScanData is defined
  useEffect(() => {
    // Always fetch, even without org - we'll use fallback data
    fetchScanData();
    // Mark opportunities as explored when user visits this page
    localStorage.setItem('viewed_opportunities', 'true');
  }, [actualId, fetchScanData]);

  // Retry button handler for error state
  const handleRetryFetch = () => {
    console.log('[RESULTS_FETCH] User clicked retry');
    fetchScanData();
  };

  const handleLinkOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowLinkerModal(true);
  };

  const handleOpportunityLinked = async (updatedOpportunity) => {
    // Fetch process name if linked
    if (updatedOpportunity.process_id) {
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/org/processes/${updatedOpportunity.process_id}`,
          {},
          activeOrganization?.organization_id
        );
        if (response.ok) {
          const processData = await response.json();
          updatedOpportunity.process_name = processData.name;
        }
      } catch (error) {
        console.error('Error fetching process name:', error);
      }
    }
    
    // Update the opportunity in the local state
    setOpportunities(prev =>
      prev.map(opp =>
        opp.id === updatedOpportunity.id ? updatedOpportunity : opp
      )
    );
  };

  const handleExportPDF = async () => {
    // Check if user can export PDF
    const canExport = planLimitsFromAPI?.can_export_pdf || planLimits.can_export_pdf;
    
    if (!canExport) {
      setShowPaywall(true);
      return;
    }

    // Open Report Builder Modal instead of direct export
    setShowReportBuilder(true);
  };

  const getReadinessColor = (grade) => {
    switch (grade) {
      case 'A': return 'text-[#14F195]';
      case 'B': return 'text-[#00E5C0]';
      case 'C': return 'text-[#F59E0B]';
      case 'D': return 'text-[#EF4444]';
      default: return 'text-[#718096]';
    }
  };

  // Updated labels to distinguish from Opportunity Potential
  const getReadinessLabel = (grade, label) => {
    // Use backend-provided label if available
    if (label) return label;
    // Fallback to grade-based labels
    switch (grade) {
      case 'A': return 'Highly Ready';
      case 'B': return 'Moderately Ready';
      case 'C': return 'Developing';
      case 'D': return 'Early Stage';
      default: return 'Unknown';
    }
  };
  
  // Opportunity Potential colors (different from readiness)
  const getOpportunityPotentialColor = (label) => {
    switch (label) {
      case 'Very High': return 'text-[#14F195]';
      case 'High': return 'text-[#00E5C0]';
      case 'Medium': return 'text-[#F59E0B]';
      case 'Developing': return 'text-[#718096]';
      default: return 'text-[#718096]';
    }
  };

  const getComplexityBadge = (complexity) => {
    const colors = {
      low: 'bg-[#14F195]/20 text-[#14F195]',
      medium: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      high: 'bg-[#EF4444]/20 text-[#EF4444]'
    };
    return colors[complexity] || colors.medium;
  };

  const getPhaseBadge = (phase) => {
    const colors = {
      'quick-win': 'bg-[#14F195]/20 text-[#14F195] border-[#14F195]/30',
      'medium-term': 'bg-[#00E5C0]/20 text-[#00E5C0] border-[#00E5C0]/30',
      'long-term': 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30'
    };
    const labels = {
      'quick-win': 'Quick Win',
      'medium-term': 'Medium-Term',
      'long-term': 'Long-Term'
    };
    return { color: colors[phase], label: labels[phase] };
  };

  let filteredOpportunities = opportunities;
  
  // Filter by phase
  if (selectedPhase !== 'all') {
    filteredOpportunities = filteredOpportunities.filter(opp => opp.implementation_phase === selectedPhase);
  }
  
  // Filter by department (future-proofing for when opportunities have department data)
  if (selectedDepartment !== 'all') {
    filteredOpportunities = filteredOpportunities.filter(opp => 
      opp.department === selectedDepartment || opp.category === selectedDepartment
    );
  }
  
  // V2.2: Filter by integration certainty
  if (selectedCertainty !== 'all') {
    filteredOpportunities = filteredOpportunities.filter(opp => opp.integrationCertainty === selectedCertainty);
  }

  const quickWins = opportunities.filter(o => o.implementation_phase === 'quick-win');
  const mediumTerm = opportunities.filter(o => o.implementation_phase === 'medium-term');
  const longTerm = opportunities.filter(o => o.implementation_phase === 'long-term');

  // V2.2: Integration Certainty grouping
  const directOpportunities = opportunities.filter(o => o.integrationCertainty === 'direct');
  const conditionalOpportunities = opportunities.filter(o => o.integrationCertainty === 'conditional');
  const patternOpportunities = opportunities.filter(o => o.integrationCertainty === 'pattern');
  // Fallback for opportunities without integrationCertainty (older data)
  const ungroupedOpportunities = opportunities.filter(o => !o.integrationCertainty);

  // Loading state
  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
          <div className="text-center">
            <CircleNotch size={48} className="text-[#00E5C0] animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">
              {hasProcessingParam ? 'Preparing your results...' : 'Loading your results...'}
            </p>
          </div>
        </div>
      </>
    );
  }

  // Error state - DO NOT REDIRECT, show error UI
  if (fetchError && !isStillGenerating) {
    return (
      <>
        <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
              <CircleNotch size={32} className="text-[#F59E0B]" />
            </div>
            <h2 className="text-white text-xl font-semibold mb-2">We saved your scan</h2>
            <p className="text-[#A0AEC0] mb-6">{fetchError.message}</p>
            <div className="space-y-3">
              <button
                onClick={handleRetryFetch}
                className="w-full px-6 py-3 rounded-full bg-[#00E5C0] text-white font-medium"
              >
                Retry Loading Results
              </button>
              <button
                onClick={() => {
                  redirectLocked.current = false;
                  navigate('/assessments');
                }}
                className="w-full px-6 py-3 rounded-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white"
              >
                Back to My Assessments
              </button>
            </div>
          </div>
        </div>
        {/* Show success modal even on error if success=1 */}
      </>
    );
  }

  // No data and no error - show skeleton state while processing
  if (!scanData) {
    // If processing, show simple loading state with banner
    if (isStillGenerating || hasProcessingParam) {
      return (
        <>
          <div className="max-w-7xl mx-auto p-6">
            {/* Processing Banner - Single consolidated banner */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 glass rounded-2xl p-6 border border-[#00E5C0]/30 bg-[#00E5C0]/10"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00E5C0]/20 flex items-center justify-center">
                  <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg">
                    Analysis in Progress
                    {generationCount > 0 && (
                      <span className="ml-3 text-sm font-normal text-[#00E5C0]">
                        ({generationCount} opportunities identified)
                      </span>
                    )}
                  </h3>
                  <p className="text-[#A0AEC0] text-sm">
                    {generationProgress || 'Analyzing business data and identifying AI opportunities. This page will automatically update when complete.'}
                  </p>
                  <p className="text-[#718096] text-xs mt-1">
                    ⏱️ This analysis typically takes 3-5 minutes depending on the complexity of your business data.
                  </p>
                </div>
              </div>
              
              {/* Progress bar */}
              {generationCount > 0 && (
                <div className="mt-4 bg-[rgba(255,255,255,0.1)] rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, generationCount * 10)}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-full bg-[#00E5C0] rounded-full"
                  />
                </div>
              )}
            </motion.div>
          </div>
        </>
      );
    }
    
    // Not processing, truly not found
    return (
      <>
        <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
          <div className="text-center">
            <p className="text-white text-lg mb-4">Assessment not found for this organization</p>
            <button
              onClick={() => {
                redirectLocked.current = false;
                navigate('/assessments');
              }}
              className="px-6 py-3 rounded-full bg-[#00E5C0] text-white"
            >
              Back to My Assessments
            </button>
          </div>
        </div>
        {/* Show success modal even with no data if success=1 */}
      </>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto">
          {/* Still Generating Banner (V2.2: Dynamic target, no hardcoded count) */}
          {isStillGenerating && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 glass rounded-2xl p-6 border border-[#00E5C0]/30 bg-[#00E5C0]/10"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00E5C0]/20 flex items-center justify-center">
                  <CircleNotch size={24} className="text-[#00E5C0] animate-spin" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg">
                    Analysis in Progress
                    {generationCount > 0 && (
                      <span className="ml-3 text-sm font-normal text-[#00E5C0]">
                        ({generationCount} opportunities identified)
                      </span>
                    )}
                  </h3>
                  <p className="text-[#A0AEC0] text-sm">
                    {generationProgress || 'Analyzing business data and identifying AI opportunities. This page will automatically update when complete.'}
                  </p>
                  <p className="text-[#718096] text-xs mt-1">
                    ⏱️ This analysis typically takes 3-5 minutes depending on the complexity of your business data.
                  </p>
                </div>
              </div>
              
              {/* Progress bar - V2.2: Phase-based, not target-based */}
              {generationCount > 0 && (
                <div className="mt-4 bg-[rgba(255,255,255,0.1)] rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, generationCount * 10)}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-full bg-[#00E5C0] rounded-full"
                  />
                </div>
              )}
            </motion.div>
          )}
          
          {/* Upgrade Banner for Starter Users */}
          {isFreePlan() && (
            <UpgradeBanner targetPlan="builder" context="scanner" onUpgrade={() => setShowUpgradeModal(true)} />
          )}

          {/* Success Banner */}
          {showSuccessBanner && (
            <AssessmentSuccessBanner
              onDismiss={handleDismissBanner}
              totalOpportunities={totalOpportunityCount}
              assessmentId={actualId}
            />
          )}

          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  {scanData.company_name} <span className="gradient-text">AI Opportunity Report</span>
                </h1>
                <p className="text-[#A0AEC0]">
                  {scanData.industry} • {scanData.headcount}
                </p>
              </div>

              {/* Export PDF moved to /reports page */}
              <button
                onClick={() => navigate('/reports')}
                className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white font-medium transition-colors flex items-center gap-2"
              >
                <FileText size={18} />
                Generate Report
              </button>
            </div>
          </motion.div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {/* AI Readiness - Locked for free users */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`glass rounded-2xl p-6 card-glow relative ${isFreePlan() ? 'opacity-60' : ''}`}
            >
              {isFreePlan() && (
                <div className="absolute inset-0 bg-[#161B26]/70 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10"
                  onClick={() => setShowPaywall(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="text-center">
                    <Lock size={32} className="text-[#00E5C0] mx-auto mb-2" />
                    <p className="text-sm text-white font-medium">Builder Feature</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#14F195]/20 to-[#00E5C0]/20 flex items-center justify-center">
                  <Trophy size={24} className="text-[#14F195]" />
                </div>
                <Tooltip text="AI Readiness Grade measures your organization's preparedness to adopt AI solutions. It considers data completeness, tooling maturity, and process documentation. This is separate from individual opportunity potential.">
                  <div className={`text-3xl font-bold cursor-help ${getReadinessColor(scanData.readiness_grade)}`}>
                    {scanData.readiness_grade || 'B'}
                  </div>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-[#A0AEC0]">AI Readiness Grade</p>
                <Tooltip text="AI Readiness Grade measures your organization's preparedness to adopt AI solutions. It considers data completeness, tooling maturity, and process documentation. This is separate from individual opportunity potential.">
                  <div className="text-[#A0AEC0] cursor-help text-xs">ⓘ</div>
                </Tooltip>
              </div>
              <p className="text-white font-medium">{getReadinessLabel(scanData.readiness_grade || 'B', scanData.readiness_label)}</p>
              <div className="mt-3 w-full h-2 bg-[#1A1B23] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#00E5C0] to-[#14F195]"
                  style={{ width: `${scanData.ai_readiness_score || 75}%` }}
                ></div>
              </div>
              {/* Context note for low readiness */}
              {scanData.ai_readiness_score > 0 && scanData.ai_readiness_score < 50 && (
                <p className="text-xs text-[#F59E0B] mt-2">
                  Tip: Add more process details to improve readiness score
                </p>
              )}
            </motion.div>

            {/* ROI - Locked for free users */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`glass rounded-2xl p-6 card-glow relative ${isFreePlan() ? 'opacity-60' : ''}`}
            >
              {isFreePlan() && (
                <div className="absolute inset-0 bg-[#161B26]/70 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10"
                  onClick={() => setShowPaywall(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="text-center">
                    <Lock size={32} className="text-[#00E5C0] mx-auto mb-2" />
                    <p className="text-sm text-white font-medium">Builder Feature</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5C0]/20 to-[#00E5C0]/20 flex items-center justify-center">
                  <CurrencyDollar size={24} className="text-[#00E5C0]" />
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">Potential Annual Savings</p>
              <p className="text-3xl font-bold text-white">
                ${scanData.total_savings_annual?.toLocaleString() || '127,500'}
              </p>
            </motion.div>

            {/* Opportunities Count */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 card-glow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5C0]/20 to-[#00D4B0]/20 flex items-center justify-center">
                  <Target size={24} className="text-[#00E5C0]" />
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">Opportunities Identified</p>
              <p className="text-3xl font-bold text-white">{totalOpportunityCount}</p>
              {isFreePlan() && (
                <p className="text-xs text-[#F59E0B] mt-2">Showing 1 of {totalOpportunityCount}</p>
              )}
            </motion.div>

            {/* Quick Wins */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass rounded-2xl p-6 card-glow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B]/20 to-[#EF4444]/20 flex items-center justify-center">
                  <Lightning size={24} className="text-[#F59E0B]" />
                </div>
              </div>
              <p className="text-sm text-[#A0AEC0]">Quick Wins</p>
              <p className="text-3xl font-bold text-white">{quickWins.length}</p>
            </motion.div>
          </div>

          {/* Enhanced Roadmap Visual */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass rounded-2xl p-8 mb-12"
          >
            <RoadmapVisual
              opportunities={opportunities}
              aiReadinessScore={scanData?.ai_readiness_score || 0}
              readinessLabel={scanData?.readiness_label || 'Developing'}
              readinessPillarScores={scanData?.readiness_pillar_scores || {}}
              isBasicTier={isBasicTier}
              onUpgrade={() => setShowUpgradeModal(true)}
              onViewAll={(phase) => setSelectedPhase(phase)}
              onViewOpportunity={(id) => {
                const opp = opportunities.find(o => o.id === id);
                if (opp) setSelectedOpportunity(opp);
              }}
            />
          </motion.div>

          {/* Opportunities List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">All Opportunities</h2>

              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedPhase('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedPhase === 'all'
                      ? 'bg-[#00E5C0] text-white'
                      : 'glass text-[#A0AEC0] hover:text-white'
                  }`}
                >
                  All ({opportunities.length})
                </button>
                <button
                  onClick={() => setSelectedPhase('quick-win')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedPhase === 'quick-win'
                      ? 'bg-[#14F195]/20 text-[#14F195] border border-[#14F195]/30'
                      : 'glass text-[#A0AEC0] hover:text-white'
                  }`}
                >
                  Quick Wins ({quickWins.length})
                </button>
                <button
                  onClick={() => setSelectedPhase('medium-term')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedPhase === 'medium-term'
                      ? 'bg-[#00E5C0]/20 text-[#00E5C0] border border-[#00E5C0]/30'
                      : 'glass text-[#A0AEC0] hover:text-white'
                  }`}
                >
                  Medium-Term ({mediumTerm.length})
                </button>
                <button
                  onClick={() => setSelectedPhase('long-term')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedPhase === 'long-term'
                      ? 'bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30'
                      : 'glass text-[#A0AEC0] hover:text-white'
                  }`}
                >
                  Long-Term ({longTerm.length})
                </button>
                
                {/* V2.2: Integration Certainty Filter - Only show if any opportunities have certainty */}
                {(directOpportunities.length > 0 || conditionalOpportunities.length > 0 || patternOpportunities.length > 0) && (
                  <>
                    <div className="h-6 w-px bg-[rgba(255,255,255,0.1)] mx-2" />
                    <button
                      onClick={() => setSelectedCertainty(selectedCertainty === 'direct' ? 'all' : 'direct')}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        selectedCertainty === 'direct'
                          ? 'bg-[#14F195]/20 text-[#14F195] border border-[#14F195]/30'
                          : 'glass text-[#A0AEC0] hover:text-white'
                      }`}
                      title="Ready to Deploy - known source and destination systems"
                    >
                      <Rocket size={12} />
                      Ready ({directOpportunities.length})
                    </button>
                    <button
                      onClick={() => setSelectedCertainty(selectedCertainty === 'conditional' ? 'all' : 'conditional')}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        selectedCertainty === 'conditional'
                          ? 'bg-[#00E5C0]/20 text-[#00E5C0] border border-[#00E5C0]/30'
                          : 'glass text-[#A0AEC0] hover:text-white'
                      }`}
                      title="Adaptable - choose systems during setup"
                    >
                      <GearSix size={12} />
                      Adaptable ({conditionalOpportunities.length})
                    </button>
                    <button
                      onClick={() => setSelectedCertainty(selectedCertainty === 'pattern' ? 'all' : 'pattern')}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        selectedCertainty === 'pattern'
                          ? 'bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30'
                          : 'glass text-[#A0AEC0] hover:text-white'
                      }`}
                      title="Automation Patterns - reusable across systems"
                    >
                      <Stack size={12} />
                      Patterns ({patternOpportunities.length})
                    </button>
                  </>
                )}
                
                {/* Department Filter */}
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="px-4 py-2 rounded-full text-sm font-medium glass text-white focus:outline-none focus:ring-2 focus:ring-[#00E5C0] cursor-pointer"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="all" className="bg-[#1a1a2e] text-white">All Departments</option>
                  <option value="Operations" className="bg-[#1a1a2e] text-white">Operations</option>
                  <option value="Sales" className="bg-[#1a1a2e] text-white">Sales</option>
                  <option value="Marketing" className="bg-[#1a1a2e] text-white">Marketing</option>
                  <option value="Finance" className="bg-[#1a1a2e] text-white">Finance</option>
                  <option value="Customer Service" className="bg-[#1a1a2e] text-white">Customer Service</option>
                  <option value="HR" className="bg-[#1a1a2e] text-white">HR</option>
                </select>
              </div>
            </div>

            {/* Subtle Pro Notice Banner for Basic Users */}
            {isBasicTier && totalOpportunityCount > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 px-4 py-3 rounded-xl bg-[#00E5C0]/10 border border-[#00E5C0]/20 flex items-center gap-3"
              >
                <Lock size={16} className="text-[#00E5C0]" />
                <p className="text-sm text-[#A0AEC0]">
                  <span className="text-white font-medium">{totalOpportunityCount - 1} additional opportunities</span> available on Builder
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="ml-auto text-sm text-[#00E5C0] hover:text-[#00D4B0] font-medium transition-colors"
                >
                  Compare plans →
                </button>
              </motion.div>
            )}

            <div className="space-y-4">
              {filteredOpportunities
                .sort((a, b) => b.priority_score - a.priority_score)
                .map((opportunity, index) => {
                  // Show locked opportunity for free/basic users (after first one)
                  // Also check if opportunity is marked as locked from backend
                  const isLocked = opportunity.locked === true || (isBasicTier && index > 0);
                  
                  return (
                    <OpportunityCard
                      key={opportunity.id}
                      id={opportunity.id}
                      title={opportunity.title}
                      description={opportunity.description}
                      impact={opportunity.impact}
                      effort={opportunity.effort}
                      priority_score={opportunity.priority_score}
                      roi={opportunity.roi}
                      monthly_savings={opportunity.cost_savings_monthly}
                      annual_savings={opportunity.cost_savings_annual}
                      time_saved={opportunity.hours_saved_monthly}
                      status={opportunity.status}
                      category={opportunity.category}
                      source="assessment"
                      linked_agent_id={opportunity.linked_agent_id}
                      linked_agent_name={opportunity.linked_agent_name}
                      automation_status={opportunity.automation_status}
                      opportunity_potential_label={opportunity.opportunity_potential_label}
                      readiness_context_note={opportunity.readiness_context_note}
                      iao={opportunity.iao}
                      expected_outcomes={opportunity.expected_outcomes}
                      workflow_fit={opportunity.workflow_fit}
                      required_capabilities={opportunity.required_capabilities || []}
                      optional_capabilities={opportunity.optional_capabilities || []}
                      locked={isLocked}
                      onUnlock={() => setShowUpgradeModal(true)}
                      onViewDetails={isLocked ? undefined : () => navigate(`/opportunities/${opportunity.id}`)}
                      index={index}
                    />
                  );
                })}
            </div>
          </motion.div>

          {/* CTA Section - Tier-aware messaging */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-12 glass rounded-3xl p-12 text-center"
          >
            {isProOrHigher() ? (
              // Pro/Partner Pro users - encourage agent creation
              <>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Ready to <span className="gradient-text">Build Your AI Workforce</span>?
                </h2>
                <p className="text-[#A0AEC0] text-lg mb-8 max-w-2xl mx-auto">
                  You have full access to create production-ready workers from these opportunities. Start automating your workflows today.
                </p>

                <div className="flex gap-4 justify-center flex-wrap">
                  <button
                    onClick={() => navigate('/workers/new')}
                    className="relative overflow-hidden rounded-full group"
                  >
                    <div className="absolute inset-0 bg-[#00E5C0]"></div>
                    <span className="relative z-10 flex items-center justify-center gap-2 px-8 py-4 text-white font-semibold">
                      <Rocket size={20} />
                      Create Worker
                    </span>
                  </button>

                  <button
                    onClick={() => navigate('/workers')}
                    className="px-8 py-4 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all flex items-center gap-2"
                  >
                    <Stack size={20} />
                    View Workers
                  </button>
                </div>

                <p className="text-[#6B7280] text-sm mt-6">
                  Click &quot;Create Worker&quot; on any opportunity above to start building
                </p>
              </>
            ) : (
              // Basic tier - encourage upgrade
              <>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Ready to <span className="gradient-text">Implement These Opportunities</span>?
                </h2>
                <p className="text-[#A0AEC0] text-lg mb-8 max-w-2xl mx-auto">
                  Turn these AI opportunities into production-ready agents that automate your workflows
                </p>

                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => navigate('/contact')}
                    className="relative overflow-hidden rounded-full group"
                  >
                    <div className="absolute inset-0 bg-[#00E5C0]"></div>
                    <span className="relative z-10 flex items-center justify-center gap-2 px-8 py-4 text-white font-semibold">
                      Get Started
                      <ArrowRight size={20} />
                    </span>
                  </button>

                  <button
                    onClick={() => navigate('/pricing')}
                    className="px-8 py-4 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
                  >
                    View Plans
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      
      {/* Paywall Modal */}
      <PaywallModal 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)}
        opportunityCount={totalOpportunityCount}
      />

      {/* Upgrade Modal - Opens in-app for locked opportunities */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={userPlan || 'basic'}
        message={`Unlock ${totalOpportunityCount - 1} Additional Opportunities`}
        feature="Full AI Opportunity Analysis"
        showComparison={true}
      />

      {/* Opportunity Linker Modal */}
      <OpportunityLinkerModal
        isOpen={showLinkerModal}
        onClose={() => {
          setShowLinkerModal(false);
          setSelectedOpportunity(null);
        }}
        opportunity={selectedOpportunity}
        onLinked={handleOpportunityLinked}
      />

      {/* Report Builder Modal */}
      <ReportBuilderModal
        isOpen={showReportBuilder}
        onClose={() => setShowReportBuilder(false)}
        organizationId={activeOrganization?.organization_id}
        onGenerate={() => {
          console.log('Report generated successfully');
        }}
      />

      {/* 
        ═══════════════════════════════════════════════════════════════════════════════
        SUCCESS MODAL - Shown when ?success=1 query param is present
        
        This is the ONLY place the completion modal is shown.
        It stays open until user clicks a button:
        - "View Results" -> closes modal, stays on page
        - "Run Another Scan" -> navigates to wizard
        - "Map a Process" -> navigates to process mapping (Pro only)
        - Basic tier: "Map a Process" is locked, shows upgrade modal
        ═══════════════════════════════════════════════════════════════════════════════
      */}
    </>
  );
};

export default AssessmentResults;
